import { Component, computed, inject, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { HELP_CENTER_URL } from '../../molecules/getting-started/getting-started.component';
import { LanguageSwitcherComponent } from '../../molecules/language-switcher/language-switcher.component';
import { BreadcrumbComponent } from '../../molecules/breadcrumb/breadcrumb.component';
import { ContextNavComponent } from '../../molecules/context-nav/context-nav.component';
import { ContextNavStore } from '../../molecules/context-nav/services/context-nav.store';
import type { ContextNavDropdownItem } from '../../molecules/context-nav/context-nav.interfaces';
import { SidebarListComponent } from '../../molecules/sidebar-list/sidebar-list.component';
import type { SidebarListItem } from '../../molecules/sidebar-list/sidebar-list.interfaces';
import { SidebarMenuComponent } from '../../molecules/sidebar-menu/sidebar-menu.component';
import type {
  SidebarMenuChild,
  SidebarMenuItem,
} from '../../molecules/sidebar-menu/sidebar-menu.interfaces';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { SidebarComponent } from '../../atoms/sidebar/sidebar.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { AvatarComponent } from '../../molecules/avatar/avatar.component';
import { NavigationTabsComponent } from '../../organisms/navigation-tabs/navigation-tabs.component';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import { ThemeSelectorComponent } from '../../molecules/theme-selector/theme-selector.component';
import {
  SUPPORTED_LANGUAGE_CODES,
  SUPPORTED_LANGUAGES,
  type LanguageOption,
  ThemeStore,
  type ThemeMode,
  type ThemeOption,
  THEME_OPTIONS,
  OCP_ANALYTICS,
  TrackAction,
  ROUTE,
  TeamServiceStrategy,
  AuthorizationStrategy,
  LanguagePreferenceService,
} from 'shared';

@Component({
  selector: 'ocp-main-layout',
  imports: [
    LanguageSwitcherComponent,
    BreadcrumbComponent,
    ContextNavComponent,
    SidebarListComponent,
    SidebarMenuComponent,
    SidebarComponent,
    HeaderComponent,
    AvatarComponent,
    TranslatePipe,
    HlmPopoverImports,
    HlmButtonImports,
    NavigationTabsComponent,
    ThemeSelectorComponent,
    ThemedIconComponent,
    RouterLink,
    RouterLinkActive,
    ThemedIconComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent {
  readonly helpCenterUrl = HELP_CENTER_URL;
  readonly tocRoute = ['/', ROUTE.toc];
  readonly imprintUrl = `/${ROUTE.imprint}`;
  private readonly authorizationService = inject(AuthorizationStrategy);
  private readonly themeStore = inject(ThemeStore);
  private readonly translateService = inject(TranslateService);
  private readonly teamsService = inject(TeamServiceStrategy);
  private readonly languagePreference = inject(LanguagePreferenceService);
  readonly contextNavStore = inject(ContextNavStore);

  private readonly teams = toSignal(this.teamsService.getTeams(), { initialValue: [] });
  protected readonly showCreateTeam = computed(() => !this.teamsService.hasOwnTeam(this.teams()));

  readonly userInitials = input('');
  readonly userName = input('');
  readonly userEmail = input('');

  readonly themeMode = this.themeStore.themeMode;
  readonly themeOptions: ThemeOption[] = Object.values(THEME_OPTIONS);

  readonly languages: LanguageOption[] = SUPPORTED_LANGUAGE_CODES.map(code => ({
    code,
    name: SUPPORTED_LANGUAGES[code],
  }));
  readonly selectedLangCode = toSignal(this.translateService.onLangChange.pipe(map(e => e.lang)), {
    initialValue: this.translateService.getCurrentLang() ?? this.languages[0].code,
  });

  onLogoutClick(): void {
    this.authorizationService.logout().subscribe();
  }

  onContextNavItemSelected(event: {
    contextItemId: string;
    dropdownItem: ContextNavDropdownItem;
  }): void {
    this.contextNavStore.itemSelected$.next(event);
  }

  @TrackAction(OCP_ANALYTICS.ACTION.CREATE, OCP_ANALYTICS.TOKEN.SIDEBAR_CREATE_NEW_TEAM)
  onContextNavCreateClicked(contextItemId: string): void {
    this.contextNavStore.createClicked$.next(contextItemId);
  }

  @TrackAction(OCP_ANALYTICS.ACTION.CLICK, OCP_ANALYTICS.TOKEN.DASHBOARD_TEAM_SELECTED)
  onInlineItemClicked(item: SidebarListItem): void {
    this.contextNavStore.inlineItemSelected$.next(item);
  }

  onInlineCreateClicked(): void {
    this.contextNavStore.inlineCreateClicked$.next();
  }

  onProjectDropdownItemSelected(event: {
    contextItemId: string;
    dropdownItem: ContextNavDropdownItem;
  }): void {
    this.contextNavStore.projectSelected$.next(event.dropdownItem);
  }

  onProjectDropdownCreateClicked(): void {
    this.contextNavStore.projectCreateClicked$.next();
  }

  onSidebarMenuItemClicked(item: SidebarMenuItem): void {
    this.contextNavStore.sidebarMenuItemClicked$.next(item);
  }

  @TrackAction(OCP_ANALYTICS.ACTION.CLICK, OCP_ANALYTICS.TOKEN.TEAMS_PROJECT_SELECTED)
  onSidebarMenuChildClicked(child: SidebarMenuChild): void {
    this.contextNavStore.sidebarMenuChildClicked$.next(child);
  }

  onMobileLanguageChange(lang: LanguageOption): void {
    this.translateService.use(lang.code);
    this.languagePreference.savePreference(lang.code);
  }

  onMobileThemeChange(mode: ThemeMode): void {
    this.themeStore.setThemeMode(mode);
  }
}
